import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Disclosure } from "@headlessui/react";
import { useForm, SubmitHandler } from "react-hook-form";
import { DateTime } from "luxon";

import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/solid";

import PageHeader from "../../../common/PageHeader";
import PageBody from "../../../common/PageBody";
import Pagination from "../../../common/Pagination";
import EventLogStatusBadge from "./EventLogStatusBadge";
import LoadingCard from "../../../common/LoadingCard";
import ErrorAlert from "../../../common/ErrorAlert";
import EventDetailDialog from "./EventDetailDialog";
import { FilterDropDown, ResourceOptionType } from "src/common/FilterDropDown";

import useGetList, { SortType } from "../../../services/useGetList";
import { EventLog } from "../../../services/webservice";
import {
  TABLE_COLUMN_HEADER_TEXT,
  TABLE_COLUMN_SORT,
  TABLE_COLUMN_TEXT,
  TEXT_INPUT,
} from "../../../utils/styles";
import { parseDate, parseClassNames } from "../../../utils/Parse";
import Button from "src/common/buttons/Button";
import FormError from "src/common/FormError";

const eventLevels: ResourceOptionType[] = [
  { id: "status-all", title: "All", filter: "" },
  { id: "status-error", title: "Error", filter: "Error" },
  { id: "status-info", title: "Info", filter: "Info" },
  { id: "status-warning", title: "Warning", filter: "Warning" },
];

const eventTypes: ResourceOptionType[] = [
  { id: "type-all", title: "All", filter: "" },
  { id: "type-processorders", title: "ProcessOrders", filter: "ProcessOrders" },
  { id: "type-cancelorders", title: "CancelOrders", filter: "CancelOrders" },
  { id: "type-inventorysync", title: "InventorySync", filter: "InventorySync" },
  {
    id: "type-updatetracking",
    title: "UpdateTracking",
    filter: "UpdateTracking",
  },
  {
    id: "type-refreshcartitems",
    title: "RefreshCartItems",
    filter: "RefreshCartItems",
  },
  {
    id: "type-submitOrdersToWms",
    title: "SubmitOrdersToWms",
    filter: "SubmitOrdersToWms",
  },
];

interface IDateSearchFormProps {
  startTime: string;
}

export default function EventLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  let pageParam = searchParams.get("page");
  const { integrationConfigId } = useParams();
  const [levelParam, setLevelParam] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState(eventLevels[0]);
  const [typeParam, setTypeParam] = useState<string>("");
  const [selectedType, setSelectedType] = useState(eventTypes[0]);
  const [startTime, setStartTime] = useState<string>("");
  const [filter, setFilter] = useState<string>(
    `&IntegrationConfigId=${integrationConfigId}`
  );
  const [sort, setSort] = useState<SortType[]>([
    { field: "startTime", direction: "desc" },
  ]);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [refreshKey, setRefreshKey] = useState<number>(1);
  const { data, error, loading, totalRecords, totalPages } =
    useGetList<EventLog>(
      "/EventLogs",
      page,
      pageSize,
      refreshKey,
      sort,
      filter
    );

  const dateForm = useForm<IDateSearchFormProps>();

  useEffect(() => {
    let newFilter = `&IntegrationConfigId=${integrationConfigId}`;
    newFilter += levelParam ? `&EventLogMaxLevel=${levelParam}` : "";
    newFilter += typeParam ? `&EventLogType=${typeParam}` : "";
    newFilter += startTime ? `&StartTime=${startTime}` : "";
    setFilter(newFilter);
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelParam, typeParam, startTime]);

  useEffect(() => {
    if (pageParam && !isNaN(Number(pageParam))) {
      handlePageChange(Number(pageParam));
    } else {
      setSearchParams({ page: page.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam]);

  const refreshData = () => setRefreshKey(refreshKey + 1);

  const handlePageChange = (page: number) => {
    setPage(page);
    setSearchParams({ page: page.toString() });
  };

  const handleSort = (field: string) => {
    let newSort: SortType = { field, direction: "asc" };
    if (sort[0].field === newSort.field) {
      newSort.direction = sort[0].direction === "asc" ? "desc" : "asc";
    }
    setSort([newSort]);
  };

  const onDateSubmit: SubmitHandler<IDateSearchFormProps> = (data) => {
    const localDate = DateTime.fromISO(data.startTime).toString();
    const utcDate = DateTime.fromISO(localDate, {
      zone: "utc",
    }).toString();
    setStartTime(encodeURIComponent(utcDate));
  };

  const handleEventLevelFilter = (option: string) => {
    setLevelParam(option);
    // setSelected based on the matching resources object from the array
    let selectedResource = eventLevels.find((stat) => stat.filter === option);
    setSelectedLevel(selectedResource || eventLevels[0]);
  };

  const handleEventTypeFilter = (option: string) => {
    setTypeParam(option);
    // setSelected based on the matching resources object from the array
    let selectedResource = eventTypes.find((stat) => stat.filter === option);
    setSelectedType(selectedResource || eventTypes[0]);
  };

  if (error)
    return (
      <div className="py-2 sm:py-10">
        <PageHeader
          title="Event Log"
          icon={<ClipboardDocumentListIcon className="w-8 md:w-10 ml-2" />}
        />
        <ErrorAlert error={error} />
      </div>
    );

  return (
    <div className="py-2 sm:py-10">
      <PageHeader
        title="Event Logs"
        icon={<ClipboardDocumentListIcon className="w-8 md:w-10 ml-2" />}
        refresh={refreshData}
        description="Status of scheduled events and user interactions."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 sm:pt-2">
        <div className="w-full rounded-2xl bg-white p-2">
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full justify-between rounded-lg bg-primary-100 px-4 py-2 text-left text-sm font-medium text-primary-900 hover:bg-primary-200 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75">
                  <span>Filter Events</span>
                  <ChevronUpIcon
                    className={`${
                      open ? "rotate-180 transform" : ""
                    } h-5 w-5 text-primary-500`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500 flex space-x-10">
                  <div className="z-10 w-full sm:w-64">
                    <FilterDropDown
                      name="eventLevel"
                      label="By Level"
                      options={eventLevels}
                      resourceParam={levelParam}
                      selected={selectedLevel}
                      handleResourceFilter={handleEventLevelFilter}
                    />
                  </div>
                  <div className="z-10 w-full sm:w-64">
                    <FilterDropDown
                      name="eventType"
                      label="By Type"
                      options={eventTypes}
                      resourceParam={typeParam}
                      selected={selectedType}
                      handleResourceFilter={handleEventTypeFilter}
                    />
                  </div>
                  <div>
                    <form onSubmit={dateForm.handleSubmit(onDateSubmit)}>
                      <div className="mb-1">
                        <label
                          htmlFor="startTime"
                          className="text-sm text-gray-500 mb-1"
                        >
                          By Date (events on/after this date)
                        </label>
                      </div>
                      <div className="flex flex-wrap space-x-4 space-y-2 sm:space-y-0">
                        <div className="flex rounded-md shadow-sm">
                          <div className="relative flex flex-grow items-stretch focus-within:z-10">
                            <input
                              type="text"
                              {...dateForm.register("startTime", {
                                required: true,
                                pattern: {
                                  value:
                                    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/i,
                                  message: "Date format must be YYYY-MM-DD",
                                },
                              })}
                              id="startTime"
                              className={parseClassNames(
                                "block w-full focus:outline-none rounded-none rounded-l-md  sm:text-sm",
                                dateForm.formState.errors.startTime
                                  ? TEXT_INPUT.error
                                  : TEXT_INPUT.valid
                              )}
                              placeholder="YYYY-MM-DD"
                            />
                          </div>
                          <button
                            type="submit"
                            className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            <span>Search</span>
                          </button>
                        </div>
                        <Button
                          label="clear search"
                          design="text"
                          func={() => {
                            setStartTime("");
                            dateForm.reset();
                          }}
                        />
                      </div>
                      {dateForm.formState.errors.startTime && (
                        <FormError
                          type="error"
                          message={
                            dateForm.formState.errors.startTime.message || ""
                          }
                        />
                      )}
                    </form>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>
      <PageBody table>
        <div className="flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="relative overflow-hidden shadow md:rounded-lg">
                <table className="min-w-full table-fixed divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className={parseClassNames(
                          TABLE_COLUMN_HEADER_TEXT.base,
                          "min-w-[7rem]"
                        )}
                      >
                        Event Id
                      </th>
                      <th scope="col" className={TABLE_COLUMN_HEADER_TEXT.base}>
                        Level
                      </th>
                      <th scope="col" className={TABLE_COLUMN_HEADER_TEXT.base}>
                        <button
                          onClick={() => handleSort("startTime")}
                          className="group inline-flex font-semibold text-gray-900"
                        >
                          Date
                          <span
                            className={parseClassNames(
                              "ml-2 rounded flex-none",
                              sort[0].field === "startTime"
                                ? TABLE_COLUMN_SORT.active
                                : TABLE_COLUMN_SORT.innactive
                            )}
                          >
                            {sort[0].field === "startTime" &&
                            sort[0].direction === "desc" ? (
                              <ChevronDownIcon className="h-5 w-5" />
                            ) : (
                              <ChevronUpIcon className="h-5 w-5" />
                            )}
                          </span>
                        </button>
                      </th>
                      <th scope="col" className={TABLE_COLUMN_HEADER_TEXT.base}>
                        <button
                          onClick={() => handleSort("eventLogType")}
                          className="group inline-flex font-semibold text-gray-900"
                        >
                          Type
                          <span
                            className={parseClassNames(
                              "ml-2 rounded flex-none",
                              sort[0].field === "eventLogType"
                                ? TABLE_COLUMN_SORT.active
                                : TABLE_COLUMN_SORT.innactive
                            )}
                          >
                            {sort[0].field === "eventLogType" &&
                            sort[0].direction === "desc" ? (
                              <ChevronDownIcon className="h-5 w-5" />
                            ) : (
                              <ChevronUpIcon className="h-5 w-5" />
                            )}
                          </span>
                        </button>
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Options</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={7}>
                          <LoadingCard />
                        </td>
                      </tr>
                    ) : (
                      data.map((event, index) => (
                        <tr key={index}>
                          <td className={TABLE_COLUMN_TEXT.base}>
                            {event.eventLogId}
                          </td>
                          <td className={TABLE_COLUMN_TEXT.base}>
                            <EventLogStatusBadge
                              text={event.eventLogMaxLevel || ""}
                              inProgress={Boolean(
                                event.eventLogStatus === "InProgress"
                              )}
                            />
                          </td>
                          <td className={TABLE_COLUMN_TEXT.base}>
                            <time dateTime={parseDate(event.startTime)}>
                              {parseDate(event.startTime)}
                            </time>
                          </td>
                          <td className={TABLE_COLUMN_TEXT.base}>
                            <span className="font-medium text-grey-900">
                              {event.eventLogType}
                            </span>
                          </td>
                          <td
                            className={parseClassNames(
                              TABLE_COLUMN_TEXT.base,
                              "text-right"
                            )}
                          >
                            <EventDetailDialog
                              id={event.eventLogId}
                              integrationConfigId={integrationConfigId}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  setPage={handlePageChange}
                  totalPages={totalPages}
                />
              </div>
            </div>
          </div>
        </div>
      </PageBody>
    </div>
  );
}
